<div class="page-head">
  <div>
    <p class="eyebrow">ค่าใช้จ่าย</p>
    <h1>รายการเบิกค่าใช้จ่ายทั่วไป</h1>
    <p>ติดตามใบขอเบิกที่ไม่ผูกกับ Booking หรือ Tour</p>
  </div>
  @if (canManageGeneralExpenses()) {
    <button
      type="button"
      class="tw-button inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 active:scale-[0.98]"
      (click)="openCreateModal()"
    >
      <lucide-icon name="Plus" [size]="17" /> เบิกค่าใช้จ่ายทั่วไป
    </button>
  }
</div>

<section class="rounded-xl border border-slate-200 bg-white shadow-sm">
  <p-table
    #generalExpensesTable
    [value]="expenses()"
    dataKey="id"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [globalFilterFields]="[
      'expenseNo',
      'expenseNameSnapshot',
      'description',
      'requestedBy',
      'status',
    ]"
    [rowHover]="true"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="แสดง {first} ถึง {last} จาก {totalRecords} รายการ"
    [tableStyle]="{ 'min-width': '1120px' }"
  >
    <ng-template #caption>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label class="relative block w-full max-w-md">
          <lucide-icon
            name="Search"
            [size]="17"
            class="pointer-events-none absolute top-1/2 left-2 z-10 -translate-y-1/2 text-slate-400"
          />
          <input
            pInputText
            #expenseQuery
            class="h-11 w-full pr-3 text-sm placeholder:text-slate-400"
            style="padding-left: 2.75rem; border-radius: 0.5rem"
            placeholder="ค้นหาเลขที่ใบเบิก ประเภท รายละเอียด หรือผู้ขอเบิก"
            (input)="generalExpensesTable.filterGlobal(expenseQuery.value, 'contains')"
          />
        </label>
        <app-refresh-button
          class="self-end sm:self-auto"
          label="รีเฟรชรายการเบิกค่าใช้จ่ายทั่วไป"
          [loading]="isLoading()"
          (refresh)="refreshExpenses()"
        />
      </div>
    </ng-template>

    <ng-template #header>
      <tr>
        <th class="w-14 text-center text-xs whitespace-nowrap">ลำดับ</th>
        <th pSortableColumn="expenseNo" class="w-48 text-xs whitespace-nowrap">
          เลขที่ใบเบิก <p-sortIcon field="expenseNo" />
        </th>
        <th pSortableColumn="expenseNameSnapshot" class="min-w-48 text-xs whitespace-nowrap">
          ประเภทค่าใช้จ่าย <p-sortIcon field="expenseNameSnapshot" />
        </th>
        <th pSortableColumn="description" class="min-w-72 text-xs whitespace-nowrap">
          รายละเอียด <p-sortIcon field="description" />
        </th>
        <th
          pSortableColumn="actualTransferAmount"
          class="w-40 text-right text-xs whitespace-nowrap"
        >
          ยอดที่ต้องโอน <p-sortIcon field="actualTransferAmount" />
        </th>
        <th pSortableColumn="requestedBy" class="min-w-44 text-xs whitespace-nowrap">
          ผู้ขอเบิก <p-sortIcon field="requestedBy" />
        </th>
        <th class="w-44 text-xs whitespace-nowrap">สถานะ</th>
        <th class="w-32 text-right text-xs whitespace-nowrap">ดำเนินการ</th>
      </tr>
    </ng-template>

    <ng-template #body let-row let-rowIndex="rowIndex">
      <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
        <td class="py-3.5 text-center text-[13px] text-slate-400 tabular-nums align-top">
          {{ rowIndex + 1 }}
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold whitespace-nowrap text-sky-700">
            {{ row.expenseNo }}
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-medium leading-5 text-slate-700">
            {{ row.expenseNameSnapshot }}
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm leading-5 text-slate-800">{{ row.description }}</p>
          @if (row.attachmentUrl) {
            <a
              [href]="row.attachmentUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 no-underline hover:underline"
            >
              เอกสาร
              <lucide-icon name="ExternalLink" [size]="12" />
            </a>
          }
          @if (row.transferSlipUrl) {
            <a
              [href]="row.transferSlipUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-1 ml-3 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 no-underline hover:underline"
            >
              สลิปโอนจ่าย
              <lucide-icon name="ExternalLink" [size]="12" />
            </a>
          }
        </td>
        <td class="py-3.5 text-right align-top">
          <p class="m-0 text-sm font-semibold whitespace-nowrap text-slate-800 tabular-nums">
            {{ row.actualTransferAmount | number: '1.2-2' }} บาท
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-medium leading-5 text-slate-700">{{ row.requestedBy }}</p>
        </td>
        <td class="py-3.5 align-top">
          <app-status-badge
            [status]="row.status"
            [displayLabel]="
              row.status === 'VERIFIED' && row.expenseNameSnapshot === 'ค่าคืนเงินลูกค้า'
                ? 'เสร็จสิ้น'
                : undefined
            "
          />
        </td>
        <td class="py-3.5 align-top">
          <div class="flex justify-end gap-1.5">
            <button
              type="button"
              class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
              title="ดูรายละเอียด"
              (click)="selectedExpense.set(row)"
            >
              <lucide-icon name="Eye" [size]="15" />
            </button>
            @if (canManageGeneralExpenses() && row.status === 'DRAFT') {
              <button
                type="button"
                class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
                title="แก้ไข"
                (click)="openEditModal(row)"
              >
                <lucide-icon name="Pencil" [size]="15" />
              </button>
            }
            @if (canManageGeneralExpenses() && ['DRAFT', 'REJECTED'].includes(row.status)) {
              <button
                type="button"
                class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white p-0 text-red-600 hover:bg-red-50"
                title="ลบ"
                (click)="deleteExpense(row)"
              >
                <lucide-icon name="Trash2" [size]="15" />
              </button>
            }
            @if (
              canManageGeneralExpenses() && row.status === 'WAITING_CLEARING' && !row.receiptPath
            ) {
              <button
                type="button"
                class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-sky-200 bg-white p-0 text-sky-700 hover:bg-sky-50"
                title="ส่งใบเสร็จ"
                aria-label="ส่งใบเสร็จ"
                (click)="openReceiptModal(row)"
              >
                <lucide-icon name="Upload" [size]="15" />
              </button>
            }
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td colspan="8">
          <div class="empty">
            <h3>ไม่พบรายการเบิกค่าใช้จ่ายทั่วไป</h3>
            <p>ลองเปลี่ยนคำค้นหา</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</section>

@if (isModalOpen()) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeModal()"
  >
    <section
      class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="general-expense-modal-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            ข้อมูลค่าใช้จ่าย
          </p>
          <h2 id="general-expense-modal-title" class="m-0 text-xl font-bold text-slate-800">
            {{
              editingExpense()
                ? 'แก้ไขรายการเบิกค่าใช้จ่ายทั่วไป'
                : 'สร้างรายการเบิกค่าใช้จ่ายทั่วไป'
            }}
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            บันทึกรายการที่ไม่เกี่ยวข้องกับ Booking หรือ Tour
          </p>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeModal()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>

      <form [formGroup]="expenseForm" (ngSubmit)="saveExpense(false)">
        <div class="max-h-[68vh] space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <section class="rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
            <div class="mb-4 flex items-center gap-3 border-b border-slate-200 pb-3">
              <span class="grid size-9 place-items-center rounded-lg bg-sky-100 text-sky-700">
                <lucide-icon name="ReceiptText" [size]="18" />
              </span>
              <div>
                <h3 class="m-0 text-base font-bold text-slate-800">รายละเอียดรายการเบิก</h3>
                <p class="mt-0.5 mb-0 text-xs text-slate-500">
                  ระบุประเภท บัญชีรับเงิน รายละเอียด และยอดที่ต้องโอนจริง
                </p>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-x-6 gap-y-0 md:grid-cols-2">
              <label
                class="grid content-start gap-1 text-sm font-semibold text-slate-700 md:col-span-2"
              >
                <span>ประเภทค่าใช้จ่าย <span class="text-red-500">*</span></span>
                <p-select
                  class="h-11 w-full font-normal"
                  formControlName="expenseTypeId"
                  [options]="expenseTypeOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  filterBy="label"
                  filterPlaceholder="ค้นหาประเภทค่าใช้จ่าย"
                  placeholder="เลือกประเภทค่าใช้จ่าย"
                  [loading]="isLoadingExpenseTypes()"
                  [disabled]="isLoadingExpenseTypes() || expenseTypeOptions().length === 0"
                />
                <span class="min-h-4">
                  @if (isInvalid('expenseTypeId')) {
                    <small class="font-normal text-red-600">กรุณาเลือกประเภทค่าใช้จ่าย</small>
                  }
                </span>
              </label>

              <label
                class="grid content-start gap-1 text-sm font-semibold text-slate-700 md:col-span-2"
              >
                <span>ข้อมูลบัญชีรับเงิน <span class="text-red-500">*</span></span>
                <p-select
                  class="h-11 w-full font-normal"
                  formControlName="paymentAccountSource"
                  [options]="paymentAccountSourceOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="เลือกข้อมูลบัญชีรับเงิน"
                  [class.p-invalid]="isInvalid('paymentAccountSource')"
                  (onChange)="onPaymentAccountSourceChange($event.value)"
                />
                <span class="min-h-4">
                  @if (isInvalid('paymentAccountSource')) {
                    <small class="font-normal text-red-600">กรุณาเลือกข้อมูลบัญชีรับเงิน</small>
                  }
                </span>
              </label>

              @if (expenseForm.controls.paymentAccountSource.value === 'OTHER_BANK_ACCOUNT') {
                <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
                  <span>ธนาคาร <span class="text-red-500">*</span></span>
                  <p-select
                    class="h-11 w-full font-normal"
                    formControlName="bankName"
                    [options]="bankOptions"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    filterBy="label"
                    filterPlaceholder="ค้นหาธนาคาร"
                    placeholder="เลือกธนาคาร"
                    [class.p-invalid]="isInvalid('bankName')"
                  />
                  <span class="min-h-4">
                    @if (isInvalid('bankName')) {
                      <small class="font-normal text-red-600">กรุณาเลือกธนาคาร</small>
                    }
                  </span>
                </label>

                <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
                  <span>เลขที่บัญชี <span class="text-red-500">*</span></span>
                  <input
                    pInputText
                    class="h-11 w-full font-normal"
                    formControlName="accountNumber"
                    inputmode="numeric"
                    placeholder="กรอกเลขที่บัญชี 6–20 หลัก"
                    [class.p-invalid]="isInvalid('accountNumber')"
                  />
                  <span class="min-h-4">
                    @if (isInvalid('accountNumber')) {
                      <small class="font-normal text-red-600">
                        กรุณากรอกเลขที่บัญชีเป็นตัวเลข 6–20 หลัก
                      </small>
                    }
                  </span>
                </label>
              }

              <label
                class="grid content-start gap-1 text-sm font-semibold text-slate-700 md:col-span-2"
              >
                <span>รายละเอียด <span class="text-red-500">*</span></span>
                <textarea
                  pTextarea
                  class="min-h-24 w-full resize-y font-normal"
                  formControlName="description"
                  placeholder="ระบุรายละเอียดที่ต้องการขอเบิก"
                  [autoResize]="true"
                ></textarea>
                <span class="min-h-4">
                  @if (isInvalid('description')) {
                    <small class="font-normal text-red-600">กรุณากรอกรายการ</small>
                  }
                </span>
              </label>
              <label
                class="grid content-start gap-1 text-sm font-semibold text-slate-700 md:col-span-2"
              >
                <span>ยอดเงิน <span class="text-red-500">*</span></span>
                <p-inputnumber
                  class="w-full font-normal"
                  inputStyleClass="h-11 w-full"
                  formControlName="actualTransferAmount"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                  [min]="1"
                  placeholder="0.00"
                />
                <small class="font-normal text-slate-500">กรอกยอดเต็มไม่ต้องหัก ณ ที่จ่าย</small>

                <span class="min-h-4">
                  @if (isInvalid('actualTransferAmount')) {
                    <small class="font-normal text-red-600">กรุณากรอกจำนวนเงินมากกว่า 0</small>
                  }
                </span>
              </label>
              <div
                class="grid content-start gap-2 text-sm font-semibold text-slate-700 md:col-span-2"
              >
                <span>ไฟล์อ้างอิงบัญชีรับเงิน <span class="text-red-500">*</span></span>
                <div class="rounded-xl border border-dashed border-slate-300 bg-white p-4">
                  <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <p-fileupload
                      #attachmentUpload
                      mode="basic"
                      name="attachment"
                      chooseLabel="เลือกไฟล์"
                      accept=".pdf,.jpg,.jpeg,.png"
                      [maxFileSize]="5000000"
                      [auto]="false"
                      (onSelect)="selectAttachment($event)"
                      (onClear)="clearAttachment()"
                    />
                    <button
                      type="button"
                      class="tw-button inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                      [disabled]="!attachmentUpload.files.length"
                      (click)="attachmentUpload.clear(); clearAttachment()"
                    >
                      <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                    </button>
                  </div>
                  @if (attachmentName()) {
                    <p class="mt-2 mb-0 text-xs font-normal text-sky-700">
                      {{ attachmentName() }}
                    </p>
                  }
                  <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                    รองรับ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                  </p>
                </div>
                @if (!attachmentName() && expenseForm.touched) {
                  <small class="font-normal text-red-600">กรุณาแนบไฟล์อ้างอิงบัญชีรับเงิน</small>
                }
              </div>
            </div>
          </section>
        </div>

        <div
          class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
        >
          <button
            type="button"
            class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
            (click)="closeModal()"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
            [disabled]="isSaving()"
            (click)="saveExpense(false)"
          >
            <lucide-icon name="Save" [size]="18" /> บันทึกร่าง
          </button>
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)]"
            [disabled]="isSaving()"
            (click)="saveExpense(true)"
          >
            <lucide-icon name="Send" [size]="18" /> ส่งเบิก
          </button>
        </div>
      </form>
    </section>
  </div>
}

@if (selectedExpense(); as expense) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="selectedExpense.set(null)"
  >
    <section
      class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="general-expense-detail-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            {{ expense.expenseNo }}
          </p>
          <h2 id="general-expense-detail-title" class="m-0 text-xl font-bold text-slate-800">
            รายละเอียดรายการเบิก
          </h2>
          <div class="mt-2">
            <app-status-badge
              [status]="expense.status"
              [displayLabel]="
                expense.status === 'VERIFIED' && expense.expenseNameSnapshot === 'ค่าคืนเงินลูกค้า'
                  ? 'เสร็จสิ้น'
                  : undefined
              "
            />
          </div>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="selectedExpense.set(null)"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>
      <div class="p-5 sm:p-6">
        <dl
          class="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div>
            <dt class="text-xs font-semibold text-slate-500">เลขที่รายการ</dt>
            <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.expenseNo }}</dd>
          </div>
          <div>
            <dt class="text-xs font-semibold text-slate-500">ประเภทค่าใช้จ่าย</dt>
            <dd class="mt-1 mb-0 font-semibold text-slate-800">
              {{ expense.expenseNameSnapshot }}
            </dd>
          </div>
          <div>
            <dt class="text-xs font-semibold text-slate-500">ผู้ขอเบิก</dt>
            <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.requestedBy }}</dd>
          </div>
          <div>
            <dt class="text-xs font-semibold text-slate-500">จำนวนเงิน</dt>
            <dd class="mt-1 mb-0 font-semibold text-emerald-700">
              {{ expense.actualTransferAmount | number: '1.2-2' }} บาท
            </dd>
          </div>
          <div class="sm:col-span-2">
            <dt class="text-xs font-semibold text-slate-500">ข้อมูลบัญชีรับเงิน</dt>
            <dd class="mt-1 mb-0 font-semibold text-slate-800">
              @if (expense.paymentAccountSource === 'OTHER_BANK_ACCOUNT') {
                {{ expense.bankName || 'ไม่ระบุธนาคาร' }} ·
                {{ expense.accountNumber || 'ไม่ระบุเลขที่บัญชี' }}
              } @else if (expense.paymentAccountSource === 'REFERENCE_FILE') {
                ตามไฟล์อ้างอิง
              } @else {
                —
              }
            </dd>
          </div>
          <div class="sm:col-span-2 lg:col-span-3">
            <dt class="text-xs font-semibold text-slate-500">รายละเอียด</dt>
            <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.description }}</dd>
          </div>
          <div class="sm:col-span-2 lg:col-span-1">
            <dt class="text-xs font-semibold text-slate-500">เอกสารประกอบ</dt>
            @if (expense.attachmentUrl) {
              <dd class="mt-1 mb-0">
                <a
                  [href]="expense.attachmentUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 font-semibold text-sky-700 no-underline hover:underline"
                >
                  เอกสาร
                  <lucide-icon name="ExternalLink" [size]="13" />
                </a>
              </dd>
            } @else {
              <dd class="mt-1 mb-0 font-semibold text-slate-400">ไม่มีเอกสารประกอบ</dd>
            }
          </div>
          <div class="sm:col-span-2 lg:col-span-1">
            <dt class="text-xs font-semibold text-slate-500">สลิปโอนจ่ายจากบัญชี</dt>
            @if (expense.transferSlipUrl) {
              <dd class="mt-1 mb-0">
                <a
                  [href]="expense.transferSlipUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 font-semibold text-sky-700 no-underline hover:underline"
                >
                  เปิดสลิปโอนจ่าย
                  <lucide-icon name="ExternalLink" [size]="13" />
                </a>
              </dd>
            } @else {
              <dd class="mt-1 mb-0 font-semibold text-slate-400">ยังไม่มีสลิปโอนจ่าย</dd>
            }
          </div>
          <div class="sm:col-span-2 lg:col-span-1">
            <dt class="text-xs font-semibold text-slate-500">ใบเสร็จ</dt>
            @if (expense.receiptFileUrl) {
              <dd class="mt-1 mb-0">
                <a
                  [href]="expense.receiptFileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 font-semibold text-sky-700 no-underline hover:underline"
                >
                  เอกสาร
                  <lucide-icon name="ExternalLink" [size]="13" />
                </a>
              </dd>
            } @else {
              <dd class="mt-1 mb-0 font-semibold text-slate-400">ยังไม่มีใบเสร็จ</dd>
            }
          </div>
        </dl>
      </div>
      <div class="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
          (click)="selectedExpense.set(null)"
        >
          ปิด
        </button>
      </div>
    </section>
  </div>
}

@if (selectedReceiptExpense(); as expense) {
  <div
    class="fixed inset-0 z-[210] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeReceiptModal()"
  >
    <section
      class="my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="general-expense-receipt-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            {{ expense.expenseNo }}
          </p>
          <h2 id="general-expense-receipt-title" class="m-0 text-xl font-bold text-slate-800">
            ส่งใบเสร็จ
          </h2>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
          [disabled]="isSavingReceipt()"
          (click)="closeReceiptModal()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>
      <div class="p-5 sm:p-6">
        <div class="grid gap-1 text-sm font-semibold text-slate-700">
          <span>แนบไฟล์ใบเสร็จ <span class="text-red-500">*</span></span>
          <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <p-fileupload
                #receiptUpload
                mode="basic"
                name="expenseReceipt"
                chooseLabel="เลือกไฟล์"
                accept=".pdf,.jpg,.jpeg,.png"
                [maxFileSize]="5000000"
                [auto]="false"
                (onSelect)="selectReceipt($event)"
                (onClear)="receiptFile.set(null); receiptFileName.set('')"
              />
              <button
                type="button"
                class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                [disabled]="!receiptUpload.files.length || isSavingReceipt()"
                (click)="receiptUpload.clear(); receiptFile.set(null); receiptFileName.set('')"
              >
                <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
              </button>
            </div>
            @if (receiptFileName()) {
              <p class="mt-2 mb-0 text-xs font-normal text-sky-700">{{ receiptFileName() }}</p>
            }
            <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
              รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
            </p>
          </div>
        </div>
      </div>
      <div
        class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
      >
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
          [disabled]="isSavingReceipt()"
          (click)="closeReceiptModal()"
        >
          ยกเลิก
        </button>
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          [disabled]="!receiptFile() || isSavingReceipt()"
          (click)="saveReceipt()"
        >
          <lucide-icon name="Check" [size]="18" />
          {{ isSavingReceipt() ? 'กำลังบันทึก...' : 'บันทึกใบเสร็จ' }}
        </button>
      </div>
    </section>
  </div>
}
